import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import { useGet } from '../lib/api/hooks';
import { CaseDetailsModel } from '../lib/models/CaseDetailsModel';
import 'moment-timezone';
import moment from 'moment';
import { toast } from 'react-toastify';
import CaseDetailsHeader from '../components/CaseDetails/CaseDetailsHeader';
import CaseDetailsGeneralInfo from '../components/CaseDetails/CaseDetailsGeneralInfo';
import CaseProcedureListing from '../components/CaseDetails/CaseProcedure/CaseProcedureListing';
import ClinicalReviewActivityListing from '../components/CaseDetails/ClinicalActivity/ClinicalReviewActivityListing';
import { CaseProcedureModel } from '../lib/models/CaseProcedureModel';
import { post } from '../lib/api';
import { PhysicianReviewRationalModel } from '../lib/models/PhysicianReviewRationalModel';
import { ClinicalReviewActivityModel } from '../lib/models/ClinicalReviewActivityModel';
import SubmitReviewModal, {
    SubmitReviewModalResult
} from '../components/CaseDetails/Modals/SubmitReviewModal';
import { PhysicianReviewModel } from '../lib/models/PhysicianReviewModel';
import CaseMessages from '../components/CaseDetails/CaseMessages/CaseMessages';
import PhysicianReviewFirstPart from '../components/CaseDetails/PhysicianReviewFirstPart';
import BlueRibbon from '../components/CaseDetails/BlueRibbon';
import { CaseStages, CrmClientsNames } from '../lib/constants/misc';
import { useScreenSmallerThan } from '../lib/hooks/breakpoint-hooks';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import useTrackEvent from '../lib/hooks/use-track-event';
import useTrackException from '../lib/hooks/use-track-exception';
import { backdropStyle } from '../style';
import { StyledCaseDetailsButtons, StyledCaseDetailsLoadingButton } from '../styledComponents';
import PhysicianReviewFirstPartSpanish from '../components/CaseDetails/PhysicianReviewFirstPartSpanish';
import PhysicianReviewSecondPartSpanish from '../components/CaseDetails/PhysicianReviewSecondPartSpanish';
import PhysicianReviewSecondPart from '../components/CaseDetails/PhysicianReviewSecondPart';
import { DecisionReasonModel } from '../lib/models/DecisionReasonModel';
import CosignReview from '../components/CaseDetails/CosignReview';
import { GlobalContextActionTypes } from '../lib/contexts/GlobalContextProvider/actions';
import { useGlobalContextDispatch } from '../lib/contexts/GlobalContextProvider';
import LeavePrompt from '../components/CaseDetails/LeavePrompt';
import { ApprovalStatuses } from '../components/CaseDetails/CaseProcedure/InitialReviewProcedureItem/InitialReviewProcedureItem';
import { UpheldStatus } from '../components/CaseDetails/CaseProcedure/AppealProcedureItem';
import { isAxiosError } from 'axios';
import { ClinicalReviewSystemTime, SystemLoggedTime } from '../lib/models/SystemLoggedTime';
import { getEnv } from '../lib/helper/get-env';
import { mdNurseDeterminationComparer } from '../lib/helper/md-nurse-determination-comparer';
import DeterminationDisagreementSection from '../components/CaseDetails/DeterminationDisagreementSection';
import DeterminationDisagreementModal from '../components/CaseDetails/Modals/DeterminationDisagreementModal/DeterminationDisagreementModal';

const AUTO_SAVE_TIMER_LENGTH = 30_000;

function CaseDetailsPage() {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
    const [resolved, setResolved] = React.useState(true);
    const trackPageView = useTrackPageView();
    const trackEvent = useTrackEvent();
    const trackException = useTrackException();
    const { id, crmClient } = useParams();
    const { data, loading } = useGet<CaseDetailsModel>(
        `casedetails\\getcasedetails?caseId=${id}&crmId=${crmClient}&isAuditable=${true}`
    );

    const [procedures, setProcedures] = React.useState<CaseProcedureModel[] | undefined>(
        data?.procedures
    );
    const [clinicalReviews, setClinicalReviews] = React.useState<
        ClinicalReviewActivityModel[] | undefined
    >(data?.clinicalReviews);
    const [physicianReviewComment, setPhysicianReviewComment] = React.useState<
        PhysicianReviewModel | undefined
    >(data?.physicianReviewComment);

    const [currentCode, setCurrentCode] = React.useState<string>('');
    const [isReadonly, setIsReadonly] = React.useState<boolean | undefined>(
        !data?.showPhysicianRationalTextArea
    );

    const [selectedReason, setSelectedReason] = React.useState<DecisionReasonModel[]>([]);

    const globalDispatch = useGlobalContextDispatch();

    const [appealedCodeNumber, setAppealedCodeNumber] = React.useState(0);
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const navigate = useNavigate();

    const [saveShouldNavigate, setSaveShouldNavigate] = useState(false);
    const [anyChangeMade, setAnyChangeMade] = useState(false);

    const [hasDeterminationDisagreement, setHasDeterminationDisagreement] = useState(false);
    const [hasAnserwedDisagreementQuestions, setHasAnsweredDisagreementQuestions] = useState(false);
    const [disagreementQuestionsModalOpen, setDisagreementQuestionsModalOpen] = useState(false);

    const handleValidationError = useCallback(
        (errMessage: string) => {
            trackEvent('Submit or Save fails on FE side', {
                caseNumber: data?.caseNumber,
                caseStage: data?.caseStage,
                crmClientName: data?.crmClientName,
                errMessage
            });
            toast.error(errMessage, { autoClose: 5000 });
        },
        [trackEvent, data]
    );

    const postSystemTime = async (isStart: boolean) => {
        await post<SystemLoggedTime>('casedetails/system-time', {
            clinicalReviewId: id,
            isStart: isStart
        });
    };

    useEffect(() => {
        let intervalId: string | number | NodeJS.Timeout | undefined;

        if (!loading) {
            getEnv('REACT_APP_LOGIN_SESSION_TIMEOUT')
                .then((configTimeout) => Number(configTimeout))
                .then((configTimeout) => {
                    if (data?.showPhysicianRationalTextArea) {
                        intervalId = setInterval(postSystemTime, configTimeout * 60000, true);
                        postSystemTime(true);
                    } else {
                        clearInterval(intervalId);
                    }
                });

            return () => {
                clearInterval(intervalId);

                if (data?.showPhysicianRationalTextArea) {
                    postSystemTime(false);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    useEffect(() => {
        saveShouldNavigate && !anyChangeMade && navigate('/', { replace: true });
    }, [anyChangeMade, saveShouldNavigate, navigate]);

    React.useEffect(() => {
        return () => {
            if (data && data.caseNumber) {
                trackPageView('Case Details Page', {
                    caseNumber: data.caseNumber,
                    crmClientName: data.crmClientName,
                    caseStage: data.caseStage
                });
            }
        };
    }, [data, trackPageView]);

    React.useEffect(() => {
        setClinicalReviews(data?.clinicalReviews);
    }, [loading, data?.clinicalReviews]);

    React.useEffect(() => {
        setProcedures(data?.procedures);
    }, [loading, selectedReason, currentCode, data?.procedures]);

    React.useEffect(() => {
        setPhysicianReviewComment(data?.physicianReviewComment);
    }, [loading, data?.physicianReviewComment]);

    React.useEffect(() => {
        setIsReadonly(!data?.showPhysicianRationalTextArea);
    }, [loading, data?.showPhysicianRationalTextArea]);

    React.useEffect(() => {
        let count = 0;
        // eslint-disable-next-line
        data?.procedures.map((item) => {
            if (!item.isApproved) {
                count++;
            }
        });
        setAppealedCodeNumber(count);
    }, [loading, data?.procedures]);

    React.useEffect(() => {
        // If caseState is Initial, check for disagreement between MD and Nurse determinations
        if (data?.caseStage === CaseStages.Initial) {
            const initialReview = data?.clinicalReviews
                .filter((cr) => cr.activityType === 'Initial')
                .at(-1);

            if (
                initialReview?.nurseDecision &&
                physicianReviewComment?.medicalReviewDetermination
            ) {
                const doesMDNurseDeterminationAlign = mdNurseDeterminationComparer(
                    initialReview.nurseDecision,
                    physicianReviewComment.medicalReviewDetermination
                );

                setHasDeterminationDisagreement(!doesMDNurseDeterminationAlign);
            }
        }
    }, [
        data?.caseStage,
        data?.clinicalReviews,
        physicianReviewComment?.medicalReviewDetermination
    ]);

    const handleReasonDenialChange = (value: DecisionReasonModel[], crmCaseCodeId: string) => {
        setAnyChangeMade((prev) =>
            prev === false ? value.length !== selectedReason.length : prev
        );

        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        procedures!.filter(
            (e: CaseProcedureModel) => e.crmCaseCodeId === crmCaseCodeId
        )[0]!.decisionReasons = value;
        setProcedures(procedures);
        setSelectedReason(value);
        setCurrentCode(
            procedures!.filter((e: CaseProcedureModel) => e.crmCaseCodeId === crmCaseCodeId)[0]!
                .requestedCode
        );
    };

    const [reviewedMinutes, setReviewedMinutes] = React.useState<number>(0);
    const [reviewExtendReason, setReviewExtendReason] = React.useState<string>('');
    const [systemTime, setSystemTime] = React.useState<ClinicalReviewSystemTime>();

    const handleSubmitTimeChange = (reviewedMinutes: number, reviewExtendReason: string) => {
        setReviewedMinutes(reviewedMinutes);
        setReviewExtendReason(reviewedMinutes > 20 ? reviewExtendReason : '');
    };

    React.useEffect(() => {
        setReviewedMinutes(reviewedMinutes);
    }, [reviewedMinutes]);

    React.useEffect(() => {
        setReviewExtendReason(reviewedMinutes > 20 ? reviewExtendReason : '');
    }, [reviewExtendReason, reviewedMinutes]);

    function validateMMMRational(): boolean {
        if (
            physicianReviewComment?.reasonDontHaveAllInformation !== '' &&
            physicianReviewComment?.reasonDontHaveAllInformationSpanish === ''
        ) {
            handleValidationError('Please input the Information Required Spanish Reason');
            return false;
        }

        if (
            physicianReviewComment?.reasonMedicalReviewDetermination !== '' &&
            physicianReviewComment?.reasonMedicalReviewDeterminationSpanish === ''
        ) {
            handleValidationError(
                'Please input the Medical Determination Rationale Spanish Reason'
            );
            return false;
        }

        if (
            physicianReviewComment?.listExclusionCriteria !== '' &&
            physicianReviewComment?.listExclusionCriteriaSpanish === ''
        ) {
            handleValidationError(
                'Please input the Medical Policy: Are any Exclusion Criteria present? (Section II.) Spanish Reason'
            );
            return false;
        }

        if (
            physicianReviewComment?.reasonInclusionCriteria !== '' &&
            physicianReviewComment?.reasonInclusionCriteriaSpanish === ''
        ) {
            handleValidationError(
                'Please input the Medical Policy: Is the Inclusion Criteria Met (Section I.)  Spanish List'
            );
            return false;
        }

        if (
            physicianReviewComment?.reasonServiceAppropiate !== '' &&
            physicianReviewComment?.reasonServiceAppropiateSpanish === ''
        ) {
            handleValidationError(
                'Please input the Inpatient stay appropriate based on Comorbidity Scale Review Spanish Reason'
            );
            return false;
        }
        return true;
    }

    function validateInputs(): boolean {
        if (data?.caseStage === CaseStages.Initial) {
            const notSelectedDecisionReason =
                procedures?.filter(
                    (e: CaseProcedureModel) =>
                        e.isApproved !== ApprovalStatuses.Approved && e.decisionReasons.length === 0
                ) ?? [];

            const notSelectedDecision = procedures?.filter((p) => !p.isApproved) ?? [];

            const duplicateCountMap = procedures?.reduce((acc, p) => {
                const key = `${p.requestedCode}_${p.isApproved}`;
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);

            if (duplicateCountMap && Object.values(duplicateCountMap).some((count) => count > 1)) {
                handleValidationError('Duplicate CPT code. Please reach out to the help desk');
                return false;
            }

            if (notSelectedDecision.length > 0) {
                const codeNames = notSelectedDecision.map((d) => d.requestedCode);
                const lastCodeName = codeNames.pop();

                handleValidationError(
                    `Please select the Code Decision for code${
                        notSelectedDecision.length > 1 ? 's' : ''
                    } ${codeNames.join(', ')} ${
                        notSelectedDecision.length === 1 ? lastCodeName : `and ${lastCodeName}`
                    }`
                );
                return false;
            }

            if (notSelectedDecisionReason.length > 0) {
                const codeNames = notSelectedDecisionReason.map((d) => d.requestedCode);
                const lastCodeName = codeNames.pop();

                handleValidationError(
                    `Please select the Decision Reason for code${
                        notSelectedDecisionReason.length > 1 ? 's' : ''
                    } ${codeNames.join(', ')} ${
                        notSelectedDecisionReason.length === 1
                            ? lastCodeName
                            : `and ${lastCodeName}`
                    }`
                );
                return false;
            }
        } else {
            const notSelectedDecisionReason =
                procedures?.filter((e: CaseProcedureModel) =>
                    data?.caseStage === CaseStages.ClaimMedicalReview
                        ? e.isUpheld !== UpheldStatus.Upheld && e.decisionReasons.length === 0
                        : e.isUpheld !== UpheldStatus.Upheld &&
                          e.isUpheld !== UpheldStatus.Overturn &&
                          e.isUpheld !== UpheldStatus.PartialOverturn &&
                          e.isUpheld !== UpheldStatus.NoReviewRequired
                ) ?? [];

            const notSelectedDecision = procedures?.filter((p) => !p.isUpheld) ?? [];

            const duplicateCountMap = procedures?.reduce((acc, p) => {
                const key = `${p.requestedCode}_${p.isUpheld}`;
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);

            if (duplicateCountMap && Object.values(duplicateCountMap).some((count) => count > 1)) {
                handleValidationError('Duplicate CPT code. Please reach out to the help desk');
                return false;
            }

            if (notSelectedDecision.length > 0) {
                const codeNames = notSelectedDecision.map((d) => d.requestedCode);
                const lastCodeName = codeNames.pop();

                handleValidationError(
                    `Please select the Code Decision for code${
                        notSelectedDecision.length > 1 ? 's' : ''
                    } ${codeNames.join(', ')} ${
                        notSelectedDecision.length === 1 ? lastCodeName : `and ${lastCodeName}`
                    }`
                );
                return false;
            }

            if (notSelectedDecisionReason.length > 0) {
                const codeNames = notSelectedDecisionReason.map((d) => d.requestedCode);
                const lastCodeName = codeNames.pop();

                handleValidationError(
                    `Please select the Decision Reason for code${
                        notSelectedDecisionReason.length > 1 ? 's' : ''
                    } ${codeNames.join(', ')} ${
                        notSelectedDecisionReason.length === 1
                            ? lastCodeName
                            : `and ${lastCodeName}`
                    }`
                );
                return false;
            }
        }
        if (
            physicianReviewComment?.isReviewed === false ||
            physicianReviewComment?.isReviewed === undefined
        ) {
            handleValidationError("Please tick the 'I have reviewed' button");
            return false;
        }

        if (physicianReviewComment?.haveAllInformation === undefined) {
            handleValidationError(
                'Please select if you have all the information required to make a decision'
            );
            return false;
        }

        if (physicianReviewComment?.isInclusionCriteriaMet === undefined) {
            handleValidationError(
                'Please select the Medical Policy: Is the Inclusion Criteria Met (Section I.)'
            );
            return false;
        }

        if (
            physicianReviewComment?.isInclusionCriteriaMet === false &&
            physicianReviewComment?.reasonInclusionCriteria === ''
        ) {
            handleValidationError(
                'Please explain which criteria have not been met for the Medical Policy: Is the Inclusion Criteria Met? (Section I.)'
            );
            return false;
        }

        if (physicianReviewComment.isExclusionCriteriaPresent === undefined) {
            handleValidationError('Please select if there any exclusion criteria present');
            return false;
        }

        if (physicianReviewComment?.isServiceAppropiate === undefined) {
            handleValidationError(
                'Please select if the facility setting/site of service is appropriate'
            );
            return false;
        }

        if (
            physicianReviewComment?.isServiceAppropiate === false &&
            physicianReviewComment?.reasonServiceAppropiate === ''
        ) {
            handleValidationError('Please explain comorbidity screen result');
            return false;
        }

        if (
            data?.caseStage === CaseStages.Initial &&
            physicianReviewComment?.medicalReviewDetermination === ''
        ) {
            handleValidationError('Please complete your Medical Determination Rationale');
            return false;
        }

        if (physicianReviewComment?.reasonMedicalReviewDetermination === '') {
            handleValidationError('Please input the Medical Determination Rationale Reason');
            return false;
        }

        if (
            data?.caseStage === CaseStages.Initial &&
            (physicianReviewComment?.medicalReviewDetermination === 'Denied' ||
                physicianReviewComment?.medicalReviewDetermination === 'Partially Approved') &&
            (physicianReviewComment?.isAppAppropriate === null ||
                physicianReviewComment?.isAppAppropriate === undefined)
        ) {
            handleValidationError('Please select a response for APP P2P Appropriateness');
            return false;
        }

        if (
            physicianReviewComment?.isAppAppropriate === true &&
            (!physicianReviewComment?.appP2PReasonText ||
                physicianReviewComment.appP2PReasonText.trim() === '')
        ) {
            handleValidationError('Please select a Decision Reason for APP P2P.');
            return false;
        }

        return true;
    }
    const handleSave = async (shoudNavigate: boolean, hideSuccessToast?: boolean) => {
        setResolved(false);
        const physicianReviewRational: PhysicianReviewRationalModel = {
            caseProcedures: procedures ? procedures : [],
            clinicalReviews: clinicalReviews ? clinicalReviews : [],
            caseId: data ? data.caseId : '',
            caseNumber: data ? data.caseNumber : '',
            crmId: data ? data.crmClientId : '',
            reviewedMinutes: 0,
            caseStage: data ? data.caseStage : '',
            caseCategory: data ? data.category : '',
            reviewExtendReason: '',
            physicianReviewComment: physicianReviewComment ? physicianReviewComment : null,
            systemTime: systemTime ? systemTime : null
        };

        try {
            const result = await post(`casedetails/savephysicianrational`, physicianReviewRational);
            if (result.data != null) {
                trackEvent('Case saved', {
                    caseNumber: data?.caseNumber,
                    caseStage: data?.caseStage,
                    crmClientName: data?.crmClientName
                });
                setAnyChangeMade(false);
                !hideSuccessToast && toast.success('Review saved', { autoClose: 5000 });
                setSaveShouldNavigate(shoudNavigate);
                if (result.status === 200 && result.statusText === 'OK') {
                    const currentDateAndTime = new Date().toISOString();
                    setLastSavedAt(currentDateAndTime);
                }
            }
        } catch (error) {
            const err = error as Error;
            trackException(err, {
                action: 'Case save failure',
                caseNumber: data?.caseNumber,
                caseStage: data?.caseStage,
                crmClientName: data?.crmClientName,
                payload: physicianReviewRational,
                errorDetails: isAxiosError(error)
                    ? error.toJSON()
                    : { message: err.message, stack: err.stack, name: err.name }
            });

            if (err.message.indexOf('Request failed with status code 400') >= 0) {
                toast.error('Case already submitted', { autoClose: 5000 });
                setTimeout(() => window.location.reload(), 2000);
            } else {
                toast.error('An error occurred while updating the entities', { autoClose: 5000 });
                //Navigate backwards if error occurs
                setTimeout(() => navigate(-1), 3000);
            }
            console.log(error);
        } finally {
            setResolved(true);
        }
    };

    useEffect(() => {
        if (anyChangeMade) {
            // Clear any in-flight debounce timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            // Debounce: wait 30s after the last change, then save
            timerRef.current = setTimeout(() => {
                Promise.resolve(handleSave(false, true));
            }, AUTO_SAVE_TIMER_LENGTH);
            // Cleanup on dependency change/unmount
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyChangeMade]);

    const handleOpen = async () => {
        const res = await post<ClinicalReviewSystemTime>('casedetails/get-system-time', {
            clinicalReviewId: id,
            userId: data?.userId
        });
        if (res.data != null) {
            setSystemTime(res.data);
        }
        setDialogOpen(true);
    };

    const handleSubmitButton = async (data: SubmitReviewModalResult) => {
        await handleSubmit(data);
    };

    const handleSubmit = async ({
        reviewedMinutes,
        reviewExtendReason
    }: SubmitReviewModalResult) => {
        let procedureValidation = [];
        if (data?.caseStage === CaseStages.Initial) {
            procedureValidation = procedures
                ? procedures.filter(
                      (e: CaseProcedureModel) => e.deniedQuantity > 0 && e.decisionReasons === null
                  )
                : [];
        }

        if (!validateInputs()) {
            return;
        }
        if (data?.crmClientName === CrmClientsNames.MMM) {
            if (!validateMMMRational()) {
                return;
            }
        }

        if (procedureValidation.length === 0) {
            const physicianReviewRational: PhysicianReviewRationalModel = {
                caseProcedures: procedures ? procedures : [],
                clinicalReviews: clinicalReviews ? clinicalReviews : [],
                physicianReviewComment: physicianReviewComment ? physicianReviewComment : null,
                caseId: data ? data.caseId : '',
                caseNumber: data ? data.caseNumber : '',
                crmId: data ? data.crmClientId : '',
                caseStage: data ? data.caseStage : '',
                caseCategory: data ? data.category : '',
                reviewedMinutes,
                reviewExtendReason,
                systemTime: systemTime ? systemTime : null
            };

            try {
                const result = await post(
                    'casedetails/submitphysicianrational',
                    physicianReviewRational
                );
                if (result.data != null) {
                    trackEvent('Case submitted', {
                        caseNumber: data?.caseNumber,
                        caseStage: data?.caseStage,
                        crmClientName: data?.crmClientName
                    });
                    setAnyChangeMade(false);
                    toast.success('Review submitted', { autoClose: 5000 });
                    setSaveShouldNavigate(true);
                }
            } catch (error) {
                const err = error as Error;
                trackException(err, {
                    action: 'Case submit failure',
                    caseNumber: data?.caseNumber,
                    caseStage: data?.caseStage,
                    crmClientName: data?.crmClientName,
                    payload: physicianReviewRational,
                    errorDetails: isAxiosError(error)
                        ? error.toJSON()
                        : { message: err.message, stack: err.stack, name: err.name }
                });

                toast.error('An error occurred while updating the entities', { autoClose: 5000 });
                //Navigate backwards if error occurs
                setTimeout(() => navigate(-1), 2000);
                console.log(error);
            }
        } else {
            handleValidationError('Please select the denial reasons and/or fill in the review');
        }
    };

    const setHandleSaveOnGlobal = () => {
        globalDispatch({ type: GlobalContextActionTypes.SetHandleCaseSave, payload: handleSave });
    };

    React.useEffect(() => {
        const beforeunloadHandler = (e: BeforeUnloadEvent) => {
            if (anyChangeMade) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', beforeunloadHandler);

        return () => {
            window.removeEventListener('beforeunload', beforeunloadHandler);
        };
    }, [anyChangeMade]);

    React.useEffect(() => {
        setHandleSaveOnGlobal();

        return () => {
            globalDispatch({ type: GlobalContextActionTypes.SetHandleCaseSave, payload: null });
        };
    });

    return (
        <>
            {!loading ? (
                <>
                    <CaseDetailsHeader
                        caseNumber={data?.caseNumber}
                        displayDueDate={data?.displayDueDate}
                        displayDueTime={data?.displayDueTime}
                        id={data?.crmCaseId}
                        crmClient={crmClient}
                        clinicalReviewId={id}
                    />
                    {data != null ? (
                        <>
                            {data.caseStage !== CaseStages.Initial && (
                                <BlueRibbon
                                    title={data.caseStage}
                                    appealedCodeNumber={appealedCodeNumber}
                                />
                            )}

                            <CaseDetailsGeneralInfo
                                data={data}
                                isReadOnly={isReadonly}
                                isDialogOpen={dialogOpen}
                            />
                            <ClinicalReviewActivityListing clinicalReviews={clinicalReviews} />

                            {!isReadonly ? (
                                <>
                                    {data.crmClientName !== CrmClientsNames.MMM ? (
                                        <PhysicianReviewFirstPart
                                            physicianReview={physicianReviewComment}
                                            label={data?.physicianTextAreaLabel}
                                            setAnyChangeMade={setAnyChangeMade}
                                        />
                                    ) : (
                                        <PhysicianReviewFirstPartSpanish
                                            physicianReview={physicianReviewComment}
                                            label={data?.physicianTextAreaLabel}
                                            setAnyChangeMade={setAnyChangeMade}
                                        />
                                    )}
                                    <CaseProcedureListing
                                        procedureList={procedures}
                                        crmId={crmClient}
                                        crmClientName={data?.crmClientName}
                                        caseStage={data?.caseStage}
                                        handleReasonDenialChange={handleReasonDenialChange}
                                        setAnyChangeMade={setAnyChangeMade}
                                    />
                                    {data.crmClientName !== CrmClientsNames.MMM ? (
                                        <PhysicianReviewSecondPart
                                            physicianReview={physicianReviewComment}
                                            label={data?.physicianTextAreaLabel}
                                            clinicalReviews={data.clinicalReviews}
                                            clinicalReviewId={data.caseId}
                                            caseStage={data?.caseStage}
                                            setAnyChangeMade={setAnyChangeMade}
                                            setHasDeterminationDisagreement={
                                                setHasDeterminationDisagreement
                                            }
                                            handleOpenDisagreementQuestionsModal={() =>
                                                setDisagreementQuestionsModalOpen(true)
                                            }
                                        />
                                    ) : (
                                        <PhysicianReviewSecondPartSpanish
                                            physicianReview={physicianReviewComment}
                                            label={data?.physicianTextAreaLabel}
                                            caseStage={data?.caseStage}
                                            setAnyChangeMade={setAnyChangeMade}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: hasDeterminationDisagreement
                                                ? 'space-between'
                                                : 'flex-end',
                                            alignItems: 'start'
                                        }}>
                                        {hasDeterminationDisagreement ? (
                                            <DeterminationDisagreementSection
                                                clinicalReviewId={data.caseId}
                                                hasAnsweredDisagreementQuestions={
                                                    hasAnserwedDisagreementQuestions
                                                }
                                                setHasAnsweredDisagreementQuestions={
                                                    setHasAnsweredDisagreementQuestions
                                                }
                                                handleOpenDisagreementQuestionsModal={() =>
                                                    setDisagreementQuestionsModalOpen(true)
                                                }
                                            />
                                        ) : null}
                                        {lastSavedAt && (
                                            <Box sx={{ paddingRight: '20px' }}>
                                                <Typography>{`Last Saved: ${moment
                                                    .utc(lastSavedAt)
                                                    .tz('America/New_York')
                                                    .format('MM/DD/YYYY')} ${moment
                                                    .utc(lastSavedAt)
                                                    .tz('America/New_York')
                                                    .format('HH:mm:ss [EST]')} `}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        sx={
                                            !isSmallerThanSmall
                                                ? {
                                                      padding: '0 0.625rem',
                                                      margin: '0 1.25rem 0.625rem 1.25rem'
                                                  }
                                                : {
                                                      margin: '0',
                                                      padding: '0 0.625rem',
                                                      display: 'flex',
                                                      justifyContent: 'center'
                                                  }
                                        }>
                                        <Box
                                            sx={{
                                                marginBottom: '0.938rem',
                                                marginTop: '0.625rem'
                                            }}>
                                            <StyledCaseDetailsLoadingButton
                                                variant="contained"
                                                loading={!resolved}
                                                onClick={() => handleSave(true)}>
                                                Save Review
                                            </StyledCaseDetailsLoadingButton>
                                            <StyledCaseDetailsButtons
                                                variant="contained"
                                                onClick={() => {
                                                    handleOpen();
                                                }}
                                                disabled={
                                                    hasDeterminationDisagreement &&
                                                    !hasAnserwedDisagreementQuestions
                                                }>
                                                Submit Review
                                            </StyledCaseDetailsButtons>
                                        </Box>
                                        {hasDeterminationDisagreement ? (
                                            <DeterminationDisagreementModal
                                                clinicalReviewId={data.caseId}
                                                open={disagreementQuestionsModalOpen}
                                                handleClose={() =>
                                                    setDisagreementQuestionsModalOpen(false)
                                                }
                                                handleSuccesfulSave={() => {
                                                    setHasAnsweredDisagreementQuestions(true);
                                                    setDisagreementQuestionsModalOpen(false);
                                                }}
                                            />
                                        ) : null}

                                        <SubmitReviewModal
                                            open={dialogOpen}
                                            reviewExtendReason={reviewExtendReason}
                                            reviewedMinutes={reviewedMinutes}
                                            suggestedSystemTime={systemTime}
                                            handleSubmitTimeChange={handleSubmitTimeChange}
                                            onSubmit={handleSubmitButton}
                                            onClose={() => {
                                                setDialogOpen(false);
                                            }}
                                        />
                                    </Box>
                                </>
                            ) : null}
                            {data.showCosignReview && (
                                <CosignReview
                                    caseId={data.caseId}
                                    caseCategory={data.category}
                                    caseStage={data?.caseStage}
                                    reviewExtendReason={reviewExtendReason}
                                    reviewedMinutes={reviewedMinutes}
                                    handleSubmitTimeChange={handleSubmitTimeChange}
                                />
                            )}
                            <CaseMessages
                                caseId={data.crmCaseId}
                                crmClient={data.crmClientName}
                                userId={data.userId}
                                caseStage={data.caseStage}
                                isReadonly={false}
                            />
                        </>
                    ) : null}
                </>
            ) : (
                <>
                    <Backdrop sx={backdropStyle} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </>
            )}
            <LeavePrompt when={anyChangeMade} onConfirm={() => handleSave(false)} />
        </>
    );
}

export default CaseDetailsPage;
